#!/usr/bin/env node

import { logger, configureLogger } from '../utils/logger.js';
import yargs from 'yargs';
import { hideBin } from 'yargs/helpers';
import { FileSystemAdapter, realFileSystemAdapter, RootedFileSystemAdapter } from '../utils/file-system-adapter.js';
import { Arguments, Argv, CommandModule } from 'yargs';
import { handleError } from '../errors/errors';
import { LLMail } from '../LLMail.js';
import { loadLlmailConfig } from '../utils/config.js';
import { PluginCommand, CommandContext } from '../services/plugin-service.js';
import { dump } from 'js-yaml';
import path from 'path';
import { fileURLToPath } from 'url';
import { createRequire } from 'module';
import { Plugin } from '../services/plugin-service.js';
import { OperationResult } from '../types.js';
import { IntentService } from '../services/intent-service.js';
import { PathnameService } from '../services/pathname-service.js';
import { PluginService } from '../services/plugin-service.js';
import { initCommand } from '../cli/init.js';
import fs from 'fs';
import { readFileSync } from 'fs';
import { loadPlugins } from '../utils/plugin-loader.js';

// Configure logging based on debug flag immediately
const debugMode = process.argv.includes('--debug');
configureLogger(debugMode);

// Get the directory path of the current module
const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

let version = '0.0.0';
try {
  // Try to load package.json from the installed package location
  const packagePath = path.resolve(__dirname, '../../../package.json');
  const packageJson = JSON.parse(readFileSync(packagePath, 'utf8'));
  version = packageJson.version;
} catch (e) {
  try {
    // Fallback to development package.json location
    const packagePath = path.resolve(__dirname, '../../package.json');
    const packageJson = JSON.parse(readFileSync(packagePath, 'utf8'));
    version = packageJson.version;
  } catch (e) {
    // Use default version if both attempts fail
    console.error('Warning: Could not load package.json');
  }
}

// Define command builder type
type YargsCommandBuilder = (yargs: Argv) => Argv;

// Define command handler type
type YargsCommandHandler = (argv: Arguments) => Promise<void>;

// Define command module type with proper generics
type YargsCommandModule = CommandModule<{}, any>;

export interface CliResult {
  success: boolean;
  error?: string;
  id?: string;
  path?: string;
  data?: any;
}

/**
 * Registers a plugin's commands (and subcommands) onto a yargs instance.
 */
function registerPluginCommands(
  yargsInstance: typeof yargs,
  commands: PluginCommand[],
  llmail: LLMail,
  rootDir: string
) {
  logger.debug('Registering plugin commands:', commands);
  for (const cmd of commands) {
    logger.debug(`Registering command: ${cmd.name}`);
    const builder = (yargs: Argv) => {
      // Add options for this command
      if (cmd.options) {
        for (const opt of cmd.options) {
          const optionConfig: any = {
            type: opt.type || 'string',
            description: opt.description,
            default: opt.default,
            demandOption: !!opt.required
          };
          
          // Handle camelCase aliases for kebab-case options
          if (opt.name.includes('-')) {
            optionConfig.alias = opt.name.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
          }
          
          yargs.option(opt.name, optionConfig);
        }
      }

      // Recursively register subcommands (if any)
      if (cmd.subcommands && cmd.subcommands.length > 0) {
        logger.debug(`Registering subcommands for ${cmd.name}:`, cmd.subcommands);
        for (const subcmd of cmd.subcommands) {
          logger.debug(`Registering subcommand: ${subcmd.name}`);
          const subBuilder = (subyargs: Argv) => {
            if (subcmd.options) {
              for (const opt of subcmd.options) {
                const optionConfig: any = {
                  type: opt.type || 'string',
                  description: opt.description,
                  default: opt.default,
                  demandOption: !!opt.required
                };
                
                if (opt.name.includes('-')) {
                  optionConfig.alias = opt.name.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
                }
                
                subyargs.option(opt.name, optionConfig);
              }
            }
            return subyargs;
          };

          const subHandler = async (argv: Arguments) => {
            try {
              // Create command context
              const context: CommandContext = {
                llmail,
                logger,
                config: llmail.getConfig(),
                cwd: rootDir,
                pluginService: llmail.getPluginService()
              };

              // Run beforeExecute hook if present
              if (subcmd.beforeExecute) {
                const modifiedArgs = await subcmd.beforeExecute(argv);
                if (modifiedArgs) {
                  argv = { ...argv, ...modifiedArgs };
                }
              }

              // Execute command
              const result = await subcmd.execute(argv, context);

              // Run afterExecute hook if present
              if (subcmd.afterExecute) {
                await subcmd.afterExecute(result);
              }
            } catch (error) {
              // Run onError hook if present
              if (subcmd.onError && error instanceof Error) {
                const transformedError = await subcmd.onError(error, argv);
                if (transformedError) {
                  throw transformedError;
                }
              }
              throw error;
            }
          };

          yargs.command({
            command: subcmd.name,
            describe: subcmd.description,
            builder: subBuilder,
            handler: subHandler
          });
        }
      }

      return yargs;
    };

    const handler = async (argv: Arguments) => {
      try {
        // Create command context
        const context: CommandContext = {
          llmail,
          logger,
          config: llmail.getConfig(),
          cwd: rootDir,
          pluginService: llmail.getPluginService()
        };

        // Run beforeExecute hook if present
        if (cmd.beforeExecute) {
          const modifiedArgs = await cmd.beforeExecute(argv);
          if (modifiedArgs) {
            argv = { ...argv, ...modifiedArgs };
          }
        }

        // Execute command
        const result = await cmd.execute(argv, context);

        // Run afterExecute hook if present
        if (cmd.afterExecute) {
          await cmd.afterExecute(result);
        }
      } catch (error) {
        // Run onError hook if present
        if (cmd.onError && error instanceof Error) {
          const transformedError = await cmd.onError(error, argv);
          if (transformedError) {
            throw transformedError;
          }
        }
        throw error;
      }
    };

    yargsInstance.command({
      command: cmd.name,
      describe: cmd.description,
      builder,
      handler
    });
  }
}

export async function runCli(
  args: string[],
  fs: FileSystemAdapter = realFileSystemAdapter,
  rootDir: string = process.cwd(),
  plugins: Plugin[] = []
): Promise<CliResult> {
  try {
    // Handle version flag immediately, before any other processing
    if (args.includes('--version') || args.includes('-v')) {
      console.log(version);
      return { success: true };
    }

    // Remove debug flag from args to not interfere with command parsing
    const cleanArgs = args.filter(arg => arg !== '--debug');

    // Create required services for rooted fs adapter
    const fsConfig = await loadLlmailConfig(fs, rootDir);
    const pluginService = new PluginService();

    // Register core types BEFORE registering plugins
    pluginService.registerCoreTypes(fsConfig.types_list);
    pluginService.registerCoreActiveStates(fsConfig.active_states);
    pluginService.registerCoreInactiveReasons(fsConfig.inactive_reasons);

    // Load plugins from config if not provided
    if (plugins.length === 0 && fsConfig.plugins) {
      logger.debug('Loading plugins from config:', fsConfig.plugins);
      const loadedPlugins = await loadPlugins(fsConfig);
      plugins = loadedPlugins;
    }

    // Register all plugins
    for (const plugin of plugins) {
      pluginService.registerPlugin(plugin);
    }

    const intentService = new IntentService(fsConfig, pluginService);
    const pathnameService = new PathnameService(fsConfig, intentService);
    
    // Only create a new RootedFileSystemAdapter if the provided fs isn't already one
    const rootedFs = fs instanceof RootedFileSystemAdapter ? fs : new RootedFileSystemAdapter(fs, rootDir, pathnameService);
    let commandResult: CliResult = { success: true };

    const yargsInstance = yargs(cleanArgs)
      .scriptName('llmail')
      .usage('$0 <cmd> [args]')
      .version(false) // Disable default version output
      .option('debug', {
        type: 'boolean',
        description: 'Enable debug logging',
        global: true
      })
      .example('$0 init', 'Initialize a new llmail repository')
      .example('$0 init --config custom.yaml', 'Initialize with custom config path')
      .example('$0 new bug --title "Critical issue"', 'Create a new bug')
      .example('$0 mv abc123 --state working', 'Move issue to working state')
      .middleware([(argv) => {
        if (argv.version) {
          console.log(version);
          process.exit(0);
        }
      }])
      .command({
        command: 'version',
        describe: 'Show llmail version',
        handler: () => {
          console.log(version);
          process.exit(0);
        }
      })
      .command({
        command: 'init',
        describe: 'Initialize a new llmail repository',
        builder: (yargs: Argv) => {
          return yargs.option('config', {
            alias: 'c',
            type: 'string',
            description: 'Path to configuration file (default: llmail.yaml)'
          });
        },
        handler: async (argv) => {
          try {
            await initCommand(argv);
            commandResult = { success: true };
          } catch (error) {
            commandResult = { success: false, error: error instanceof Error ? error.message : String(error) };
          }
        }
      })
      .exitProcess(false)
      .strict(false)
      .help();

    // For all other commands, load config and initialize LLMail
    const configPath = args.includes('--config') ? args[args.indexOf('--config') + 1] : 
                      args.includes('-c') ? args[args.indexOf('-c') + 1] : undefined;
    const config = await loadLlmailConfig(rootedFs, rootDir, configPath);
    const llmail = new LLMail({ fs: rootedFs, config, plugins, rootDir, pluginService });
    await llmail.init();

    // Register plugin commands from the initialized LLMail instance
    const allPluginCommands = llmail.getPluginService().getAllCommands();
    logger.debug('Plugin commands:', allPluginCommands);
    if (allPluginCommands.length > 0) {
      logger.debug('Registering plugin commands');
      registerPluginCommands(yargsInstance, allPluginCommands, llmail, rootDir);
    } else {
      logger.debug('No plugin commands to register');
    }

    // Register built-in commands
    const newCommandModule: CommandModule = {
      command: 'new [type]',
      describe: 'Create a new issue',
      builder: (yargs: Argv) => {
        return yargs
          .positional('type', {
            type: 'string',
            describe: 'Type of issue (defaults to "issue")',
            default: 'issue'
          })
          .option('state', {
            alias: 's',
            type: 'string',
            description: 'State of the new item'
          })
          .option('title', {
            alias: 't',
            type: 'string',
            description: 'Title for the new item (added to frontmatter)'
          })
          .option('frontmatter', {
            alias: 'f',
            type: 'string',
            description: 'JSON string representing frontmatter metadata'
          })
          .option('content', {
            alias: 'c',
            type: 'string',
            description: 'Content for the new item'
          })
          .example('$0 new', 'Create a new issue with default type')
          .example('$0 new bug', 'Create a new bug')
          .example('$0 new feat --title "New feature"', 'Create a new feature with title');
      },
      handler: async (argv) => {
        try {
          const options: any = {};
          if (argv.title) options.title = argv.title;
          if (argv.content) options.content = argv.content;
          if (argv.state) options.state = argv.state;
          if (argv.frontmatter) {
            try {
              options.frontmatter = JSON.parse(argv.frontmatter as string);
            } catch (error) {
              throw new Error('Invalid frontmatter JSON');
            }
          }

          const id = await llmail.new(argv.type as string | undefined, options);
          logger.success(`Created new issue with ID: ${id}`);
          commandResult = { success: true, id };
        } catch (error) {
          if (error instanceof Error) {
            logger.error('Error creating new issue:', error.message);
            throw new Error(`Failed to create new issue: ${error.message}`);
          }
          throw error;
        }
      }
    };

    yargsInstance
      .command(newCommandModule)
      .command({
        command: 'mv <id>',
        aliases: ['move'],
        describe: 'Move an issue to a new state or type',
        builder: (yargs) => {
          return yargs
            .positional('id', {
              describe: 'ID of the issue',
              type: 'string'
            })
            .option('state', {
              alias: 's',
              type: 'string',
              description: 'New state'
            })
            .option('type', {
              alias: 't',
              type: 'string',
              description: 'New type'
            })
            .option('active', {
              type: 'boolean',
              description: 'Mark as active'
            })
            .option('inactive', {
              type: 'boolean',
              description: 'Mark as inactive'
            });
        },
        handler: async (argv) => {
          const result = await llmail.mv(argv.id as string, {
            state: argv.state as string,
            type: argv.type as string,
            active: argv.active as boolean,
            inactive: argv.inactive as boolean
          });
          commandResult = result;
        }
      })
      .command({
        command: 'done <id>',
        describe: 'Mark an issue as done',
        builder: (yargs) => {
          return yargs
            .positional('id', {
              describe: 'ID of the issue',
              type: 'string'
            })
            .option('state', {
              alias: 's',
              type: 'string',
              description: 'Done state'
            })
            .option('frontmatter', {
              alias: 'f',
              type: 'string',
              description: 'JSON string representing additional frontmatter metadata'
            });
        },
        handler: async (argv) => {
          const options: any = {};
          if (argv.state) {
            // Get intent service from llmail
            const intentService = llmail.getIntentService();
            
            // Validate state first
            const isValid = intentService.isValidState(argv.state);
            if (!isValid) {
              throw new Error(`Invalid state: ${argv.state}`);
            }
            
            // Then check if it's an active state
            const isActive = intentService.isActiveState(argv.state);
            if (isActive) {
              throw new Error('Cannot mark issue as done with active state');
            }
            
            options.state = argv.state;
          }
          if (argv.frontmatter) {
            options.frontmatter = JSON.parse(argv.frontmatter as string);
          }
          const result: OperationResult = await llmail.done(argv.id as string, options);
          logger.success('Marked issue as done');
          commandResult = { success: true, path: result.filepath };
        }
      })
      .command({
        command: 'update <id>',
        describe: 'Update an issue',
        builder: (yargs) => {
          return yargs
            .positional('id', {
              describe: 'ID of the issue',
              type: 'string'
            })
            .option('update', {
              alias: 'u',
              type: 'string',
              description: 'The update text to add',
              demandOption: true
            })
            .option('state', {
              alias: 's',
              type: 'string',
              description: 'State to update the item with'
            })
            .option('frontmatter', {
              alias: 'f',
              type: 'string',
              description: 'JSON string representing additional frontmatter metadata'
            })
            .option('append', {
              type: 'boolean',
              description: 'Append update to the bottom instead of prepending'
            });
        },
        handler: async (argv) => {
          const metadata: any = {};
          if (argv.state) metadata.state = argv.state;
          if (argv.frontmatter) {
            Object.assign(metadata, JSON.parse(argv.frontmatter as string));
          }
          // Get the issue first to get its file path
          const issue = await llmail.getIssueById(argv.id as string);
          if (!issue) {
            throw new Error(`Issue ${argv.id} not found`);
          }
          if (argv.append) {
            await llmail.appendUpdate(argv.id as string, argv.update as string, metadata);
            logger.success('Appended update to issue');
          } else {
            await llmail.prependUpdate(argv.id as string, argv.update as string, metadata);
            logger.success('Prepended update to issue');
          }
          commandResult = { success: true, path: issue.filePath };
        }
      })
      .command({
        command: 'sync',
        describe: 'Sync file locations with metadata',
        builder: (yargs) => {
          return yargs
            .option('dry-run', {
              alias: 'd',
              type: 'boolean',
              description: 'Preview changes without applying them'
            })
            .option('force', {
              alias: 'f',
              type: 'boolean',
              description: 'Force sync without confirmation'
            });
        },
        handler: async (argv) => {
          const options: any = {};
          if (argv['dry-run']) options.dryRun = true;
          if (argv.force) options.force = true;
          const result = await llmail.sync(options);
          logger.success('Sync completed successfully');
          if (result.changes && result.changes.length > 0) {
            logger.info('Changes:');
            result.changes.forEach(change => {
              logger.info(`ID: ${change.id}, from: ${change.from}, to: ${change.to}, state: ${change.state}`);
            });
          }
          if (result.errors && result.errors.length > 0) {
            logger.error('Errors:');
            result.errors.forEach(err => {
              logger.error(`ID: ${err.id}, error: ${err.error}`);
            });
          }
          commandResult = { success: true, data: result };
        }
      })
      .command({
        command: 'status',
        describe: 'Show current state of all files',
        builder: (yargs) => {
          return yargs
            .option('json', {
              type: 'boolean',
              description: 'Output in JSON format'
            });
        },
        handler: async (argv) => {
          const result = await llmail.sync({ dryRun: true });
          // Ensure both arrays exist
          result.changes = result.changes || [];
          result.errors = result.errors || [];
          
          if (argv.json) {
            logger.info(JSON.stringify(result, null, 2));
          } else {
            if (result.changes.length > 0) {
              logger.info('Changes needed:');
              result.changes.forEach(change => {
                logger.info(`ID: ${change.id}, from: ${change.from}, to: ${change.to}, state: ${change.state}`);
              });
            } else {
              logger.info('All files are in sync.');
            }
            
            if (result.errors.length > 0) {
              logger.error('Errors:');
              result.errors.forEach(err => {
                logger.error(`ID: ${err.id}, error: ${err.error}`);
              });
            }
          }
          commandResult = { success: true, data: result };
        }
      })
      .exitProcess(false)
      .strict(false) // Allow unknown arguments for plugin commands
      .demandCommand(1, 'You must specify a command')
      .help();

    // Parse arguments
    await yargsInstance.parseAsync();
    return commandResult;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error));
  }
}

// Execute CLI directly
if (process.argv[1].endsWith('llmail') || process.argv[1].endsWith('llmail.js') || process.argv[1].endsWith('llmail.cjs')) {
  runCli(hideBin(process.argv), realFileSystemAdapter).then((result) => {
    if (!process.env.VITEST) {
      process.exit(0);
    }
  }).catch((error) => {
    console.error(error);
    if (!process.env.VITEST) {
      process.exit(1);
    }
  });
} 